import { ActionTemplateFormValues, ChecklistStep } from 'pages/ActionLibrary/ActionLibraryCreatePage/ActionTemplateForm.types';
import { Option } from 'types/Option';

import type {
  ActionTemplate,
  ActionTemplateDetailDTO,
  ActionTemplateHistory,
  ActionTemplateHistoryDTO,
  ActionTemplateListDTO,
  ActionTemplateStep,
  ActionTemplateStepDTO,
  ActionTemplateUpdateDTO,
  AssetCategory,
  AssetCategoryDTO,
  AssetItem,
  AssetItemDTO,
  AssetOwner,
  AssetOwnerDTO,
  AvailableAssetsResponse,
  AvailableAssetsResponseDTO,
  ChoicesResponse,
  ChoicesResponseDTO,
  ActionTemplateCreatedBy,
  ActionTemplateCreatedByDTO,
  AreasResponse,
  ActionTemplateCreateDTO,
  ActionTemplateStepCreateDTO,
  RecommendedAsset,
  RecommendedAssetDTO,
  RecommendedAssetOwner,
  RecommendedAssetOwnerDTO,
  RecommendedAssetOwnerProfile,
  RecommendedAssetOwnerProfileDTO,
  RecommendedAssetCategory,
  RecommendedAssetCategoryDTO,
  RecommendedAssetsResponse,
  RecommendedAssetsResponseDTO,
  ActionTemplateSubAreaDataDTO,
  SubareaResponseDTO,
} from './actionLibrary.types';
// Step parser
export const parseActionTemplateStepForFE = (dto: ActionTemplateStepDTO): ActionTemplateStep => ({
  id: dto.id,
  title: dto.title,
  shortDescription: dto.short_description,
  criticality: dto.criticality,
  criticalityDisplay: dto.criticality_display,
  tags: dto.tags,
  reference: dto.reference,
  reportedTo: dto.reported_to,
  relevantFor: dto.relevant_for,
  status: dto.status,
  statusDisplay: dto.status_display,
  order: dto.order,
  mandatory: dto.mandatory,
});

// Asset parsers
export const parseAssetCategoryForFE = (dto: AssetCategoryDTO): AssetCategory => ({
  id: dto.id,
  name: dto.name,
});

export const parseAssetOwnerForFE = (dto: AssetOwnerDTO): AssetOwner => ({
  id: dto.id,
  email: dto.email,
  fullName: dto.full_name,
});

export const parseAssetItemForFE = (dto: AssetItemDTO): AssetItem => ({
  id: dto.id,
  name: dto.name,
  key: dto.key,
  category: dto.category ? parseAssetCategoryForFE(dto.category) : null,
  owner: parseAssetOwnerForFE(dto.owner),
  importance: dto.importance ?? null,
  type: dto.type,
});

export const parseActionTemplateCreatedByForFE = (dto: ActionTemplateCreatedByDTO): ActionTemplateCreatedBy => ({
  id: Number(dto.id),
  email: dto.email,
  fullName: dto.full_name,
});

// Action Template parsers
export const parseActionTemplateListForFE = (dto: ActionTemplateListDTO): ActionTemplate => ({
  id: dto.id,
  name: dto.name,
  shortDescription: dto.short_description,
  purpose: dto.purpose,
  purposeDisplay: dto.purpose_display,
  area: dto.area,
  areaDisplay: dto.area_display,
  areaData: dto.area_data
    ? {
        id: dto.area_data.id,
        name: dto.area_data.name,
        nameTranslationKey: dto.area_data.name_translation_key,
        code: dto.area_data.code,
        displayName: dto.area_data.display_name,
        displayNameTranslationKey: dto.area_data.display_name_translation_key,
        slug: dto.area_data.slug,
        isActive: dto.area_data.is_active,
      }
    : undefined,
  subArea: dto.sub_area,
  subAreaData: dto.sub_area_data
    ? {
        id: dto.sub_area_data.id,
        name: dto.sub_area_data.name,
        nameTranslationKey: dto.sub_area_data.name_translation_key,
        slug: dto.sub_area_data.slug,
        isActive: dto.sub_area_data.is_active,
      }
    : undefined,
  status: dto.status,
  statusDisplay: dto.status_display,
  criticality: dto.criticality,
  criticalityDisplay: dto.criticality_display,
  ownerType: dto.owner_type,
  ownerTypeDisplay: dto.owner_type_display,
  actionStatus: dto.action_status,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
  tags: dto.tags,
  steps: dto.steps?.map(parseActionTemplateStepForFE) || [],
  reference: dto.reference,
  reportedTo: dto.reported_to,
  assetCategories: dto.asset_categories?.map(parseAssetCategoryForFE) || [],
  validityPeriodMonths: dto.validity_period_months,
  isLocked: dto.is_locked,
  lockedByUser: dto.locked_by_user ? parseActionTemplateCreatedByForFE(dto.locked_by_user) : undefined,
  lockInfo: dto.lock_info,
  isFreshChange: dto.is_fresh_change,
  hasImpendingChanges: dto.has_impending_changes,
  lastPublishedAt: dto.last_published_at,
  createdBy: dto.created_by ? parseActionTemplateCreatedByForFE(dto.created_by) : undefined,
  instancesCountAllOrgs: dto.instances_count_all_orgs,
  instancesCountOrg: dto.instances_count_org,
});

export const parseActionTemplateDetailForFE = (dto: ActionTemplateDetailDTO): ActionTemplate => ({
  id: dto.id,
  name: dto.name,
  shortDescription: dto.short_description,
  purpose: dto.purpose,
  purposeDisplay: dto.purpose_display,
  area: dto.area,
  areaDisplay: dto.area_display,
  areaData: dto.area_data
    ? {
        id: dto.area_data.id,
        name: dto.area_data.name,
        nameTranslationKey: dto.area_data.name_translation_key,
        code: dto.area_data.code,
        displayName: dto.area_data.display_name,
        displayNameTranslationKey: dto.area_data.display_name_translation_key,
        slug: dto.area_data.slug,
        isActive: dto.area_data.is_active,
      }
    : undefined,
  subArea: dto.sub_area,
  subAreaData: dto.sub_area_data
    ? {
        id: dto.sub_area_data.id,
        name: dto.sub_area_data.name,
        nameTranslationKey: dto.sub_area_data.name_translation_key,
        slug: dto.sub_area_data.slug,
        isActive: dto.sub_area_data.is_active,
      }
    : undefined,
  status: dto.status,
  statusDisplay: dto.status_display,
  criticality: dto.criticality,
  criticalityDisplay: dto.criticality_display,
  ownerType: dto.owner_type,
  ownerTypeDisplay: dto.owner_type_display,
  actionStatus: dto.action_status,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
  tags: dto.tags,
  steps: dto.steps.map(parseActionTemplateStepForFE),
  validityPeriodMonths: dto.validity_period_months,
  createdBy: dto.created_by ? parseActionTemplateCreatedByForFE(dto.created_by) : undefined,
  ownerOrganization:
    typeof dto.owner_organization === 'object' && dto.owner_organization !== null && 'name' in dto.owner_organization
      ? dto.owner_organization.name
      : (dto.owner_organization as string),
  assets: dto.assets,
  assetCategories: dto.asset_categories ? dto.asset_categories.map(parseAssetCategoryForFE) : [],
  isDeleted: dto.is_deleted,
  reference: dto.reference,
  reportedTo: dto.reported_to,
  lockedByUser: dto.locked_by_user ? parseActionTemplateCreatedByForFE(dto.locked_by_user) : undefined,
  isLocked: dto.is_locked,
  lockInfo: dto.lock_info,
  isFreshChange: dto.is_fresh_change,
  hasImpendingChanges: dto.has_impending_changes,
  lastPublishedAt: dto.last_published_at,
  instancesCountAllOrgs: dto.instances_count_all_orgs,
  instancesCountOrg: dto.instances_count_org,
});

export const parseAllActionTemplatesForFE = (dtos: ActionTemplateListDTO[]): ActionTemplate[] => dtos.map(parseActionTemplateListForFE);

// History parser
export const parseActionTemplateHistoryForFE = (dto: ActionTemplateHistoryDTO): ActionTemplateHistory => ({
  id: dto.id,
  actionType: dto.action_type,
  actionTypeDisplay: dto.action_type_display,
  templateNameSnapshot: dto.template_name_snapshot,
  performedBy: dto.performed_by,
  note: dto.note,
  createdAt: dto.created_at,
});

export const parseAllActionTemplateHistoryForFE = (dtos: ActionTemplateHistoryDTO[]): ActionTemplateHistory[] =>
  dtos.map(parseActionTemplateHistoryForFE);

// Choices parser
export const parseChoicesResponseForFE = (dto: ChoicesResponseDTO): ChoicesResponse => ({
  purpose: dto.purpose,
  area: dto.area,
  status: dto.status,
  criticality: dto.criticality,
  ownerType: dto.owner_type,
});

// Available Assets parser
export const parseAvailableAssetsResponseForFE = (dto: AvailableAssetsResponseDTO): AvailableAssetsResponse => ({
  assets: dto.assets.map(parseAssetItemForFE),
});

// Areas parser - DTO structure matches frontend structure, so we can return as-is
export const parseAreasResponseForFE = (dto: AreasResponse): AreasResponse => dto;

// Recommended Assets parsers
export const parseRecommendedAssetOwnerProfileForFE = (dto: RecommendedAssetOwnerProfileDTO): RecommendedAssetOwnerProfile => ({
  id: dto.id,
  email: dto.email,
  firstName: dto.first_name,
  lastName: dto.last_name,
  imageUrl: dto.image_url,
});

export const parseRecommendedAssetOwnerForFE = (dto: RecommendedAssetOwnerDTO): RecommendedAssetOwner => ({
  id: dto.id,
  email: dto.email,
  lastLogin: dto.last_login,
  isActive: dto.is_active,
  profile: parseRecommendedAssetOwnerProfileForFE(dto.profile),
});

export const parseRecommendedAssetCategoryForFE = (dto: RecommendedAssetCategoryDTO): RecommendedAssetCategory => ({
  id: dto.id,
  name: dto.name,
  nameTranslationKey: dto.name_translation_key,
});

export const parseRecommendedAssetForFE = (dto: RecommendedAssetDTO): RecommendedAsset => ({
  id: dto.id,
  name: dto.name,
  owner: parseRecommendedAssetOwnerForFE(dto.owner),
  importance: dto.importance,
  category: parseRecommendedAssetCategoryForFE(dto.category),
  type: dto.type,
});

export const parseRecommendedAssetsResponseForFE = (dto: RecommendedAssetsResponseDTO): RecommendedAssetsResponse => ({
  count: dto.count,
  next: dto.next,
  previous: dto.previous,
  results: dto.results.map(parseRecommendedAssetForFE),
});

/** Parse subarea DTO (create response or nested sub_area_data) to frontend type. Accepts schema SubArea shape. */
export const parseSubAreaDataForFE = (dto: SubareaResponseDTO | ActionTemplateSubAreaDataDTO) => ({
  id: dto.id,
  name: dto.name,
  nameTranslationKey: dto.name_translation_key,
  slug: dto.slug,
  isActive: dto.is_active,
});

// Backend parser (for creating/updating)
export const parseActionTemplateForBE = (template: Partial<ActionTemplate>): Partial<ActionTemplateUpdateDTO> => ({
  name: template.name,
  purpose: template.purpose,
  area: template.area,
  sub_area: template.subArea || undefined,
  status: template.status,
  owner_type: template.ownerType,
  criticality: template.criticality,
  tags: Array.isArray(template.tags) ? template.tags : undefined,
  validity_period_months: template.validityPeriodMonths || undefined,
  is_deleted: template.isDeleted,
});

// Filter parser (for query params)
export const parseFiltersForActionTemplates = (filters: Record<string, any>) => {
  const params: Record<string, any> = {};

  if (filters.purpose?.length) {
    params.purpose = filters.purpose.map((f: any) => f.id || f.name || f).join(',');
  }
  if (filters.area?.length) {
    params.area = filters.area.map((f: any) => f.id || f.name || f).join(',');
  }
  if (filters.subarea?.length) {
    params.sub_area = filters.subarea.map((f: any) => f.id || f.name || f).join(',');
  }
  if (filters.status?.length) {
    params.status = filters.status.map((f: any) => f.id || f.name || f).join(',');
  }
  if (filters.criticality?.length) {
    params.criticality = filters.criticality.map((f: any) => f.id || f.name || f).join(',');
  }
  if (filters.creator?.length) {
    params.creator = filters.creator.map((f: any) => f.id || f.name || f).join(',');
  }

  return params;
};

export const parseActionTemplateStepForForm = (step: ActionTemplateStep): ChecklistStep => ({
  id: String(step.id),
  title: step.title,
  instruction: step.shortDescription,
  isMandatory: step.mandatory || false,
  relatedReading: step.reference,
  relevantFor: step.relevantFor,
});

const parseTagsForForm = (tags: Record<string, unknown>): Option[] =>
  Object.entries(tags).map(([key, value]) => ({
    key,
    label: (value as string).toLowerCase(),
    value: String(value).toLowerCase(),
  }));

export const parseActionTemplateForForm = (template: ActionTemplate): ActionTemplateFormValues => ({
  purpose: template.purpose,
  area: typeof template.area === 'string' ? template.area : String(template.area),
  subarea: template.subArea || '',
  title: template.name,
  shortDescription: template.shortDescription,
  criticality: template.criticality,
  tags: parseTagsForForm(template.tags),
  referenceRegulation: template.reference
    ? template.reference.split(', ').map(ref => ({
        key: ref,
        label: ref,
        value: ref,
      }))
    : [],
  reportedTo: template.reportedTo || '',
  assetTypes: template.assetCategories ? template.assetCategories.map(c => c.id) : [],
  checklist: template.steps ? template.steps.map(parseActionTemplateStepForForm) : [],
  validityPeriod: template.validityPeriodMonths ? String(template.validityPeriodMonths) : '',
  validityPeriodDoesNotApply: !template.validityPeriodMonths,
});

const getValidityPeriodMonths = (values: ActionTemplateFormValues): number | null => {
  if (values.validityPeriodDoesNotApply) return null;
  if (values.validityPeriod === '' || values.validityPeriod === '0') return null;
  return Number(values.validityPeriod);
};

export const parseActionTemplateFormValuesToDTO = (
  values: ActionTemplateFormValues,
): ActionTemplateCreateDTO | ActionTemplateUpdateDTO => ({
  name: values.title,
  short_description: values.shortDescription,
  purpose: values.purpose,
  area: values.area as any,
  sub_area: values.subarea || undefined,
  criticality: values.criticality || undefined,
  tags:
    values.tags.length > 0
      ? values.tags.map(tag => {
          const label = Array.isArray(tag.label) ? tag.label[0] : tag.label;
          // Use label as the primary value because key might be an index ("0", "1")
          // when tags are parsed from an array using Object.entries.
          // If label is missing, fallback to key.
          return String(label || tag.key || '').toLowerCase();
        })
      : undefined,
  steps:
    values.checklist.length > 0
      ? values.checklist.map(
          (step, index): ActionTemplateStepCreateDTO => ({
            title: step.title,
            short_description: step.instruction,
            criticality: values.criticality || undefined,
            tags: {},
            reference: step.relatedReading || undefined,
            reported_to: values.reportedTo || undefined,
            relevant_for: step.relevantFor || undefined,
            status: 'pending',
            order: index,
            mandatory: step.isMandatory,
          }),
        )
      : undefined,
  asset_ids: [],
  asset_category_ids: values.assetTypes.length > 0 ? values.assetTypes.map(key => Number(key)) : undefined,
  validity_period_months: getValidityPeriodMonths(values),
  reference:
    values.referenceRegulation.length > 0
      ? values.referenceRegulation
          .map(tag => {
            // Check if tag is an Option object
            if (typeof tag === 'object' && tag !== null && 'label' in tag) {
              const label = Array.isArray(tag.label) ? tag.label[0] : tag.label;
              return String(label || tag.key || '').toLowerCase();
            }
            // If it's already a string
            return String(tag).toLowerCase();
          })
          .join(', ')
      : undefined,
  reported_to: values.reportedTo,
});
